import { lazy, Suspense, useCallback, useMemo, useState, type ReactNode } from 'react';
import { useEmailVerification } from '../api/email-verification.queries';
import {
  EmailVerificationGateContext,
  type EmailVerificationGate,
} from '../model/email-verification-gate';

// Lazy so the dialog code stays out of the entry chunk.
const EmailVerificationDialog = lazy(() =>
  import('./EmailVerificationDialog').then((m) => ({ default: m.EmailVerificationDialog }))
);

/** Owns the single verification dialog; descendants open it through the gate hook. */
export function EmailVerificationProvider({ children }: { children: ReactNode }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading } = useEmailVerification();
  const requestVerification = useCallback(() => setDialogOpen(true), []);
  const verified = data?.emailVerified;

  const gate = useMemo<EmailVerificationGate>(
    () => ({ verified, isLoading, requestVerification }),
    [verified, isLoading, requestVerification]
  );

  return (
    <EmailVerificationGateContext.Provider value={gate}>
      {children}
      {/* Mounted only while open: the dialog code loads on demand and each open starts with fresh state. */}
      {dialogOpen && (
        <Suspense fallback={null}>
          <EmailVerificationDialog open={dialogOpen} onOpenChange={setDialogOpen} />
        </Suspense>
      )}
    </EmailVerificationGateContext.Provider>
  );
}
